<template>
    <div>
        <b-row>
            <b-col>
                <!-- <label><strong>Username: </strong></label> -->
                <!-- {{ patient.patientAppUserName }} -->
                <div v-if="isRegistered">
                    <i class="fas fa-star"></i>
                    <label><strong>PatientAppUser </strong></label>
                </div>
            </b-col>
        </b-row>
        <b-row>
            <b-col v-if="patient.preferredName">
                <label><strong>Preferred Name: </strong></label>
                {{ patient.preferredName }}
            </b-col>
            <b-col v-if="patient.pronouns">
                <label><strong>Pronouns: </strong></label>
                {{ patient.pronouns }}
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <label><strong>DOB: </strong></label>
                {{ patient.dateOfBirth | formatDate("L", false) }}
            </b-col>
            <b-col>
                <label><strong>Age: </strong></label>
                {{ patient.age }}
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <label><strong>Sex: </strong></label>
                {{ patient.sex }}
            </b-col>
            <b-col>
                <label><strong>Height: </strong></label>
                {{ patient.height }}
            </b-col>
            <b-col>
                <label><strong>Weight: </strong></label>
                {{ patient.weight }}
            </b-col>
        </b-row>
        <b-row v-if="patient.emailAddress?.length">
            <b-col>
                <label><strong>Email Address: </strong></label>
                {{ patient.emailAddress }}
                <a v-if="patient.emailAddress?.length" href="#" class="mx-1">
                    <i class="fas fa-copy" @click.prevent="copyText(patient.emailAddress)" />
                </a>
            </b-col>
        </b-row>
        <b-row>
            <b-col v-if="patient.gender">
                <label><strong>Gender: </strong></label>
                {{ patient.gender }}
            </b-col>
            <b-col v-if="patient.sexAssignedAtBirth">
                <label><strong>Sex at Birth: </strong></label>
                {{ patient.sexAssignedAtBirth }}
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <label><strong>Phone: </strong></label>
                {{ patient.phoneDisplay }}
                <template v-if="patient.phoneDisplay">
                    <a href="#" :style="phoneStyle" v-b-tooltip.hover :title="patient.phoneOptDateInfo">
                        <i class="fas fa-history" @click.prevent="openTextMessageModal('phoneNumber')"></i>
                    </a>
                    <RouterLink :to="{ name: 'CallLog', query: { phone: patient.phoneNumber } }" target="_blank">
                        <i class="fas fa-phone"></i>
                    </RouterLink>
                    <a href="#" class="mx-1"><i class="fas fa-copy"
                            @click.prevent="copyText(patient.phoneNumber)" /></a>
                </template>
            </b-col>
            <b-col>
                <label><strong>Cell: </strong></label>
                {{ patient.cellDisplay }}
                <template v-if="patient.cellDisplay">
                    <a href="#" :style="cellPhoneStyle" v-b-tooltip.hover :title="patient.cellOptDateInfo">
                        <i class="fas fa-history" @click.prevent="openTextMessageModal('cellNumber')"></i>
                    </a>
                    <RouterLink :to="{ name: 'CallLog', query: { phone: patient.cellNumber } }" target="_blank">
                        <i class="fas fa-phone"></i>
                    </RouterLink>
                    <a href="#" class="mx-1"><i class="fas fa-copy" @click.prevent="copyText(patient.cellNumber)" /></a>
                </template>
            </b-col>
        </b-row>
        <hr />
        <b-row>
            <b-col>
                <HelpPopup v-if="patient?.addresses?.length > 1" label="Other Addresses" icon="fa fa-info-circle"
                    class="pull-right" placement="right">
                    <div class="d-flex flex-column" style="gap: 6px;">
                        <PatientAddressCard v-for="pa in patient.addresses" :key="pa.id" :address="pa"
                            :hide-actions="true" class="mb-0" />
                    </div>
                </HelpPopup>
                <label>
                    <strong>
                        {{ patient?.addresses?.length > 1 ? "Primary" : "" }} Address:
                    </strong>
                </label>
                <br />
                {{ patient.address1 }}<br />
                {{ patient.address2 }}<br v-if="patient.address2" />
                {{ patient.addressCity }}, {{ patient.addressState }}
                {{ patient.addressZip }}

                <div v-if="patient?.signatureRequired"
                    class="border border-danger text-danger p-1 rounded-pill mt-2 text-center">
                    <small class="font-weight-bold">
                        <i class="fa fa-signature" />
                        Signature Required
                    </small>
                </div>
            </b-col>
        </b-row>
        <hr />
        <b-row>
            <b-col>
                <label><strong>Residence Code: </strong></label>
                {{ patient.residenceCode }}
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <label><strong>License State: </strong></label>
                {{ patient.licenseState }}
            </b-col>
            <b-col>
                <label><strong>License Number: </strong></label>
                {{ patient.licenseNumber }}
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <label><strong>Language: </strong></label>
                {{ patient.languageName }}
            </b-col>
            <b-col>
                <label><strong>HOH: </strong></label>
                {{ patient.headOfHousehold }}
            </b-col>
            <b-col>
                <label><strong>Species: </strong></label>
                {{ formatSpeciesCode(patient.speciesCode) }}
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <label><strong>Date of Last HIPAA Sig: </strong></label>
                {{ patient.hipaaDocsReceived | formatDate("MM-DD-YYYY") }}
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <label><strong>Delivery: </strong></label>
                {{ deliveryName }}
            </b-col>
        </b-row>
        <hr />
        <b-row>
            <b-col>
                <Checkbox v-model="pregnant" disabled inline>Pregnant</Checkbox>
                <Checkbox v-model="lactating" disabled inline>Lactating</Checkbox>
                <Checkbox v-model="smoker" disabled inline>Smoker</Checkbox>
                <Checkbox v-model="patient.childCaps" disabled inline>Use Safety Caps</Checkbox>
                <Checkbox v-model="patient.signatureRequired" disabled inline>Signature Required</Checkbox>
            </b-col>
        </b-row>

        <TextMessagesList ref="textMessagesListModal" :patient="patient"></TextMessagesList>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Ref, Vue } from "vue-property-decorator";
import { Patient } from "@/models/Patient";
import { DeliveryCode } from "@/models/DeliveryCode";
import TextMessagesList from "@/components/TextMessage/TextMessagesList.vue";
import Checkbox from "@/components/Inputs/Checkbox.vue";
import { PhoneOptStatus } from "@/models/OptStatus";
import { SpeciesCode } from "@/models/SpeciesCode";
import HelpPopup from "../HelpPopup.vue";
import PatientAddressCard from "./PatientAddressCard.vue";

@Component({
    name: "PatientDemographicsComponent",
    components: { TextMessagesList, Checkbox, HelpPopup, PatientAddressCard },
})
export default class PatientDemographicsComponent extends Vue {
    @Prop() private value!: Patient;
    @Ref() private textMessagesListModal!: TextMessagesList;

    private numberType: string = "";

    get patient(): Patient {
        if (!(this.value instanceof Patient))
            return Object.assign(new Patient(), this.value);
        return this.value;
    }

    get isRegistered(): any {
        return !!(this.patient.patientAppUserName);
    }

    get deliveryName(): string {
        let res = "";
        if (this.patient) res = DeliveryCode[this.patient.defaultDelivery];
        return res;
    }

    // pregnant
    get pregnant(): any {
        return !!this.patient?.pregnant;
    }

    // lactating
    get lactating(): any {
        return !!this.patient?.lactating;
    }

    // smoker
    get smoker(): any {
        return !!this.patient?.smoker;
    }

    getColorForOptStatus(status: PhoneOptStatus): string {
        switch (status) {
            case PhoneOptStatus.optIn: return "green";
            case PhoneOptStatus.optOut: return "red";
            case PhoneOptStatus.unknown:
            default: return "yellow";
        }
    }

    get phoneStyle() {
        return `color: ${this.getColorForOptStatus(this.patient.optInOptOutPhoneStatus)}`;
    }

    get cellPhoneStyle() {
        return `color: ${this.getColorForOptStatus(this.patient.optInOptOutCellPhoneStatus)}`;
    }

    copyText(text: string) {
        navigator.clipboard.writeText(text);
    }

    openTextMessageModal(numberType: string) {
        this.numberType = numberType;
        // var component = this.$refs.textMessagesListModal as TextMessagesList;
        // if (component) component.openModal(this.numberType);
        this.textMessagesListModal.openModal(this.numberType);
    }

    formatSpeciesCode(code: number) {
        return SpeciesCode[code] ?? "Other";
    }
}
</script>
